export enum RequestStage {
  DRAFT = 'DRAFT',
  AWAITING_APPROVAL = 'AWAITING_APPROVAL',
  SITE_ASSESSMENT = 'SITE_ASSESSMENT',
  CONFIGURATION = 'CONFIGURATION',
  CLOSED = 'CLOSED',
}

export interface StageTransition {
  fromStage: RequestStage | null;
  toStage: RequestStage;
  requiredPermission?: string;
}

export const STAGE_TRANSITIONS: StageTransition[] = [
  { fromStage: null, toStage: RequestStage.DRAFT },
  {
    fromStage: RequestStage.DRAFT,
    // The Draft submit is split by requestor type (PRD FR2). This row carries the
    // fail-closed default — external, through the SITE_ASSESSMENT review gate. The
    // internal shortcut to CONFIGURATION is applied by getNextStage, which is the only
    // place the split lives.
    toStage: RequestStage.SITE_ASSESSMENT,
    requiredPermission: 'CREATE_REQUEST',
  },
  {
    fromStage: RequestStage.AWAITING_APPROVAL,
    toStage: RequestStage.SITE_ASSESSMENT,
    requiredPermission: 'CREATE_REQUEST',
  },
  {
    fromStage: RequestStage.SITE_ASSESSMENT,
    toStage: RequestStage.CONFIGURATION,
    requiredPermission: 'EDIT_REQUEST',
  },
  {
    fromStage: RequestStage.CONFIGURATION,
    toStage: RequestStage.CLOSED,
    requiredPermission: 'CLOSE_REQUEST',
  },
];

/**
 * The client's view of the next stage. The server is authoritative for the actual write
 * (`POST /requests/:id/submit` computes its own target); this drives labels and copy.
 *
 * `isExternalRequestor` defaults to `true` so a caller that forgets the argument routes
 * through the review gate, which is the safe failure. See getStageDisplayName for the
 * same rule at the opposite polarity.
 */
export const getNextStage = (
  currentStage: RequestStage | null,
  isExternalRequestor: boolean = true,
): RequestStage | null => {
  // The only hops that split by requestor type (PRD FR2 / #1456). Every other hop is
  // driven by STAGE_TRANSITIONS unchanged.
  if (
    (currentStage === RequestStage.DRAFT ||
      currentStage === RequestStage.AWAITING_APPROVAL) &&
    !isExternalRequestor
  ) {
    return RequestStage.CONFIGURATION;
  }
  const transition = STAGE_TRANSITIONS.find(t => t.fromStage === currentStage);
  return transition?.toStage || null;
};

/**
 * External viewers read "In Progress" for both working stages (PRD FR7). Internal viewers
 * get a distinct label, "Configuration", naming the desk that holds the request. That is
 * for internal eyes only, so it is opt-in: `showInternalDetail` defaults to `false` and
 * callers resolve it through `showsInternalStageDetail` in `requestorBoundary.ts`, which
 * requires the viewer's roles to be loaded AND to not include `External Requestor`.
 * External viewers, and viewers whose roles never loaded — cached identity, offline,
 * mid-auth — all get the plain label.
 */
export const getStageDisplayName = (
  stage: RequestStage,
  showInternalDetail: boolean = false,
): string => {
  const displayNames: Record<RequestStage, string> = {
    [RequestStage.DRAFT]: 'Draft',
    [RequestStage.AWAITING_APPROVAL]: 'Awaiting Approval',
    [RequestStage.SITE_ASSESSMENT]: 'In Progress',
    [RequestStage.CONFIGURATION]: showInternalDetail
      ? 'Configuration'
      : 'In Progress',
    [RequestStage.CLOSED]: 'Closed',
  };
  return displayNames[stage] || stage;
};

/**
 * Viewer-aware on the same flag as getStageDisplayName, and for the same reason. Hiding
 * the internal "Configuration" label is pointless if the swatch changes colour at the
 * same moment: an external viewer would still be able to tell the two working stages apart,
 * just without being told which is which. For an external viewer both working stages
 * render the SITE_ASSESSMENT blue. Internal viewers keep the two-colour distinction.
 */
export const getStageColor = (
  stage: RequestStage,
  showInternalDetail: boolean = false,
): string => {
  const colors: Record<RequestStage, string> = {
    [RequestStage.DRAFT]: '#6B7280',
    // Distinct from Draft grey so Awaiting Approval scans as its own stage (PRD / UX).
    [RequestStage.AWAITING_APPROVAL]: '#007AFF',
    [RequestStage.SITE_ASSESSMENT]: '#3B82F6',
    [RequestStage.CONFIGURATION]: showInternalDetail ? '#F59E0B' : '#3B82F6',
    [RequestStage.CLOSED]: '#10B981',
  };
  return colors[stage] || '#6B7280';
};

/**
 * NOT the inverse of getStageDisplayName, and must not be used as one. An EXTERNAL
 * rendering of CONFIGURATION is "In Progress", which normalizes back to SITE_ASSESSMENT —
 * silently wrong by one stage, which would change step visibility and field permissions.
 * Feed this the persisted enum value, never a label read back off the UI.
 */
export const normalizeStage = (stageValue: string): RequestStage | null => {
  // First check if it's already a valid enum value
  if (Object.values(RequestStage).includes(stageValue as RequestStage)) {
    return stageValue as RequestStage;
  }

  // Map display names back to enum values
  const displayNameToEnum: Record<string, RequestStage> = {
    Draft: RequestStage.DRAFT,
    'Awaiting Approval': RequestStage.AWAITING_APPROVAL,
    'In Progress': RequestStage.SITE_ASSESSMENT,
    Configuration: RequestStage.CONFIGURATION,
    Closed: RequestStage.CLOSED,
    // Legacy display names retained for cached/legacy payloads; dropping them would
    // make normalizeStage return null and collapse step visibility.
    // "In Progress - Configuration" is DEFENSIVE ONLY, and is a weaker case than the
    // entries below it. It was the internal CONFIGURATION label on this branch alone and
    // never shipped: `development` has always rendered "Configuration". It is also not
    // reachable in practice, because normalizeStage is only ever handed the server-stamped
    // enum column (`stage` / `sys_request_stage`), never a rendered label. Keep it as
    // cheap insurance, but do not read it as evidence that such a payload exists.
    'In Progress - Configuration': RequestStage.CONFIGURATION,
    'Site Assessment': RequestStage.SITE_ASSESSMENT,
    // Also handle variations
    DRAFT: RequestStage.DRAFT,
    AWAITING_APPROVAL: RequestStage.AWAITING_APPROVAL,
    SITE_ASSESSMENT: RequestStage.SITE_ASSESSMENT,
    CONFIG: RequestStage.CONFIGURATION, // Map legacy CONFIG to CONFIGURATION
    CONFIGURATION: RequestStage.CONFIGURATION,
    CLOSED: RequestStage.CLOSED,
    Initial: RequestStage.DRAFT, // Map Initial to DRAFT
  };

  return displayNameToEnum[stageValue] || null;
};
